/**
 * <InlineTable /> — renders a query tool's tabular result inside the
 * chat panel (M22).
 *
 * Tool result shape (`render: "table"`):
 *   data = { columns: string[], rows: unknown[][], count: number }
 *
 * Click a header to sort; "Export CSV" downloads the full result.
 * Kept compact for the 320px panel.
 */

import { ArrowDown, ArrowUp, Download } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { exportToCsv } from "../../lib/export";

interface TableData {
  columns: string[];
  rows: unknown[][];
  count: number;
}

function _asTableData(data: Record<string, unknown>): TableData | null {
  const columns = data.columns;
  const rows = data.rows;
  if (!Array.isArray(columns) || !Array.isArray(rows)) return null;
  return {
    columns: columns as string[],
    rows: rows as unknown[][],
    count: typeof data.count === "number" ? data.count : rows.length,
  };
}

export function InlineTable({
  summary,
  data,
}: {
  summary: string;
  data: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const table = _asTableData(data);
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const sorted = useMemo(() => {
    if (!table) return [];
    if (sortCol === null) return table.rows;
    const copy = [...table.rows];
    copy.sort((a, b) => {
      const av = a[sortCol];
      const bv = b[sortCol];
      const cmp = String(av ?? "").localeCompare(
        String(bv ?? ""),
        undefined,
        { numeric: true },
      );
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [table, sortCol, sortDir]);

  if (!table) {
    return (
      <div className="rounded-lg border border-border-subtle bg-surface-muted px-2.5 py-2 text-xs text-text-secondary">
        {summary}
      </div>
    );
  }

  function toggleSort(i: number) {
    if (sortCol === i) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(i);
      setSortDir("asc");
    }
  }

  function doExport() {
    const objects = table!.rows.map((row) => {
      const obj: Record<string, unknown> = {};
      table!.columns.forEach((c, i) => {
        obj[c] = row[i];
      });
      return obj;
    });
    void exportToCsv(objects, "chat-result.csv");
  }

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-default">
      <div className="flex items-center justify-between border-b border-border-subtle px-2.5 py-1.5">
        <span className="text-xs text-text-secondary">
          {summary}
        </span>
        {table.rows.length > 0 && (
          <button
            type="button"
            onClick={doExport}
            className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
          >
            <Download size={10} /> CSV
          </button>
        )}
      </div>
      {table.rows.length === 0 ? (
        <p className="px-2.5 py-2 text-xs italic text-text-tertiary">
          {t("app.inline_no_matching")}
        </p>
      ) : (
        <div className="max-h-64 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-surface-muted">
              <tr>
                {table.columns.map((col, i) => (
                  <th
                    key={col}
                    onClick={() => toggleSort(i)}
                    className="cursor-pointer px-2 py-1 text-start font-medium text-text-secondary"
                  >
                    <span className="inline-flex items-center gap-0.5">
                      {col}
                      {sortCol === i &&
                        (sortDir === "asc" ? (
                          <ArrowUp size={9} />
                        ) : (
                          <ArrowDown size={9} />
                        ))}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, ri) => (
                <tr
                  key={ri}
                  className="border-t border-border-subtle"
                >
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-2 py-1 text-text-primary"
                    >
                      {String(cell ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
